'use client'

import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import {
  fetchAgencyBasicInfo,
  fetchRecipientProfile,
} from '@/context/usaspending'

import { usaspendingKeys } from './queryKeys'
import { assertToken } from './utils'

const isNotFoundError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false
  return error.message.includes('404')
}

export interface UseRecipientExistenceCheckOptions {
  recipientId: string
  enabled?: boolean
}

export const useRecipientExistenceCheck = ({
  recipientId,
  enabled = true,
}: UseRecipientExistenceCheckOptions) => {
  const { accessToken } = useAuthInfo()

  return useQuery({
    queryKey: usaspendingKeys.recipientExists(recipientId),
    queryFn: async () => {
      try {
        await fetchRecipientProfile(assertToken(accessToken), recipientId)
        return true
      } catch (error) {
        if (isNotFoundError(error)) {
          return false
        }
        throw error
      }
    },
    enabled: enabled && !!recipientId && !!accessToken,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export interface UseAgencyExistenceCheckOptions {
  agencyId: string
  enabled?: boolean
}

export const useAgencyExistenceCheck = ({
  agencyId,
  enabled = true,
}: UseAgencyExistenceCheckOptions) => {
  const { accessToken } = useAuthInfo()

  return useQuery({
    queryKey: usaspendingKeys.agencyExists(agencyId),
    queryFn: async () => {
      try {
        await fetchAgencyBasicInfo(assertToken(accessToken), agencyId)
        return true
      } catch (error) {
        if (isNotFoundError(error)) {
          return false
        }
        throw error
      }
    },
    enabled: enabled && !!agencyId && !!accessToken,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}
